import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLicenseTelegramUsername, normalizeTelegramUsername } from '../utils/licenseTelegram';
import './Dashboard.css';
import './CrackerController.css';
import './AddonPurchaseModal.css';
import { AddonModuleIcon } from './icons/DashboardIcons';

interface AddonPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  addonName: string;
  addonDescription: string;
  addonColor: string;
  /** Clé technique (ex. grabber, premiumProxy) — défaut : addonName en minuscules */
  addonKey?: string;
  onSuccess?: () => void;
  /** Pré-remplit le champ Telegram (ex. nom d'utilisateur de la licence) */
  telegramUsername?: string;
}

interface PaymentData {
  paymentId: string;
  paymentAddress: string;
  paymentAmount: number;
  paymentCurrency: string;
  expiresAt: string;
  status: 'pending' | 'completed' | 'expired';
}

/** Prix mensuels en EUR par module */
const ADDON_PRICES_EUR: Record<string, number> = {
  grabber: 99,
  mailer: 99,
  debouncer: 300,
  stripe: 300,
  premiumproxy: 50,
};

/** Montants crypto calibrés pour 99 € / mois */
const CRYPTO_BASE_99_EUR: Record<'BTC' | 'ETH' | 'SOL', number> = {
  BTC: 0.0025,
  ETH: 0.035,
  SOL: 0.85,
};

const AddonPurchaseModal: React.FC<AddonPurchaseModalProps> = ({
  isOpen,
  onClose,
  addonName,
  addonDescription,
  onSuccess,
  addonKey: addonKeyProp,
  telegramUsername: telegramUsernameProp,
}) => {
  const [selectedCrypto, setSelectedCrypto] = useState<'BTC' | 'ETH' | 'SOL'>('BTC');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(1200);

  const lockedTelegram = Boolean(telegramUsernameProp?.trim() || getLicenseTelegramUsername());

  useEffect(() => {
    const fromProp = telegramUsernameProp?.trim();
    const fromLicense = getLicenseTelegramUsername();
    const value = fromProp || fromLicense;
    if (value) {
      setTelegramUsername(normalizeTelegramUsername(value));
    }
  }, [telegramUsernameProp, isOpen]);

  const addonKey = (addonKeyProp ?? addonName).toLowerCase().replace(/\s+/g, '');

  const priceEur = useMemo(
    () => ADDON_PRICES_EUR[addonKey] ?? 99,
    [addonKey]
  );

  const getAddonCryptoPrice = (crypto: 'BTC' | 'ETH' | 'SOL') => {
    const factor = priceEur / 99;
    const value = CRYPTO_BASE_99_EUR[crypto] * factor;
    return Number(value.toFixed(crypto === 'BTC' ? 6 : 4));
  };

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);

  useEffect(() => {
    if (!paymentData || paymentData.status !== 'pending') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [paymentData]);

  useEffect(() => {
    if (!paymentData || paymentData.status !== 'pending') return;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(
          `https://glitchcloud.app/api/payment/status/${paymentData.paymentId}`
        );
        const data = await response.json();

        if (data.status === 'completed') {
          setPaymentData({ ...paymentData, status: 'completed' });
          setTimeout(() => {
            onSuccess?.();
            handleClose();
          }, 2000);
        }
      } catch (err) {
        console.error('Erreur vérification paiement:', err);
      }
    };

    const interval = setInterval(checkPaymentStatus, 10000);
    return () => clearInterval(interval);
  }, [paymentData, onSuccess]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePayment = async () => {
    const trimmed = telegramUsername.trim();
    if (!trimmed) {
      setError('Enter your Telegram username');
      return;
    }

    const normalizedTelegram = trimmed.startsWith('@') ? trimmed : `@${trimmed}`;

    setIsLoading(true);
    setError('');

    try {
      const requestBody = {
        planId: `addon-${addonKey}`,
        planName: `Add-on ${addonName}`,
        amount: getAddonCryptoPrice(selectedCrypto),
        currency: selectedCrypto,
        telegramUsername: normalizedTelegram,
        addonType: addonKey,
        priceEur,
      };

      const response = await fetch('https://glitchcloud.app/api/payment/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success) {
        setPaymentData(data.payment);
        setTimeLeft(1200);
      } else {
        setError(data.message || 'Error creating payment');
      }
    } catch (err) {
      console.error('Erreur paiement:', err);
      setError('Could not reach the payment server');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleClose = () => {
    setPaymentData(null);
    if (!lockedTelegram) {
      setTelegramUsername('');
    }
    setError('');
    setTimeLeft(1200);
    onClose();
  };

  if (!isOpen) return null;

  if (paymentData?.status === 'completed') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="dashboard-modal-overlay apm-overlay fixed inset-0 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            className="dashboard-modal-panel apm-panel"
          >
            <div className="cracker-panel p-8 text-center">
              <div className="apm-success-icon">✓</div>
              <p className="cracker-label mb-2">Payment confirmed</p>
              <h2 className="cracker-heading text-lg mb-3">Add-on activated</h2>
              <p className="text-sm text-[#a3a3a3] mb-6 leading-relaxed">
                The {addonName} add-on will be enabled shortly. Access details will be sent via Telegram.
              </p>
              <button type="button" onClick={handleClose} className="cracker-btn cracker-btn--gold">
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="dashboard-modal-overlay apm-overlay fixed inset-0 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ duration: 0.2 }}
          className="dashboard-modal-panel apm-panel relative"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleClose}
            className="dashboard-btn dashboard-btn--ghost apm-close p-2"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="cracker-panel">
            <div className="cracker-panel-header pr-12">
              <div className="cracker-panel-title">
                <div className="apm-icon-wrap" role="img" aria-label={`${addonName} module`}>
                  <AddonModuleIcon addonKey={addonKey} size={28} />
                </div>
                <div className="min-w-0">
                  <p className="cracker-label">Premium module</p>
                  <h2 className="cracker-heading">{addonName}</h2>
                  <p className="cracker-subheading hidden sm:block">{addonDescription}</p>
                </div>
              </div>
            </div>

            {!paymentData ? (
              <>
                <div className="apm-body space-y-5">
                  <p className="cracker-subheading sm:hidden text-sm">{addonDescription}</p>

                  <div className="apm-summary">
                    <h3 className="cracker-section-title mb-3">Summary</h3>
                    <div className="apm-summary-row">
                      <span>Module</span>
                      <strong>{addonName}</strong>
                    </div>
                    <div className="apm-summary-row">
                      <span>Subscription</span>
                      <strong>{formatEur(priceEur)} / month</strong>
                    </div>
                    <div className="apm-summary-row apm-summary-total">
                      <span>Due ({selectedCrypto})</span>
                      <strong>
                        {getAddonCryptoPrice(selectedCrypto)} {selectedCrypto}
                      </strong>
                    </div>
                  </div>

                  <div>
                    <label className="apm-field-label" htmlFor="apm-telegram">
                      Telegram *
                    </label>
                    <input
                      id="apm-telegram"
                      type="text"
                      value={telegramUsername}
                      onChange={(e) => setTelegramUsername(e.target.value)}
                      placeholder="@your_username"
                      className="apm-input font-mono"
                      readOnly={lockedTelegram}
                      disabled={lockedTelegram}
                    />
                    <p className="text-xs text-[#525252] mt-1.5">
                      {lockedTelegram
                        ? 'Username linked to your license — access will be delivered to this account'
                        : 'Access and license delivered to this Telegram account'}
                    </p>
                  </div>

                  <div>
                    <label className="apm-field-label">Cryptocurrency</label>
                    <div className="apm-crypto-grid">
                      {(['BTC', 'ETH', 'SOL'] as const).map((crypto) => (
                        <button
                          key={crypto}
                          type="button"
                          onClick={() => setSelectedCrypto(crypto)}
                          className={`apm-crypto-btn ${
                            selectedCrypto === crypto ? 'apm-crypto-btn--active' : ''
                          }`}
                        >
                          <div className="apm-crypto-symbol">
                            {crypto === 'BTC' ? '₿' : crypto === 'ETH' ? 'Ξ' : '◎'}
                          </div>
                          <div className="apm-crypto-code">{crypto}</div>
                          <div className="apm-crypto-amount">
                            {getAddonCryptoPrice(crypto)} {crypto}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="cracker-section cracker-section--flush !p-0 !border-0">
                    <h3 className="cracker-section-title mb-2">Included</h3>
                    <ul className="apm-features">
                      <li>Full access to {addonName}</li>
                      <li>Priority technical support</li>
                      <li>Automatic updates</li>
                      <li>Monthly renewal</li>
                    </ul>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="cracker-alert"
                    >
                      <span aria-hidden="true">⚠</span>
                      <span>{error}</span>
                    </motion.div>
                  )}
                </div>

                <div className="apm-footer">
                  <button
                    type="button"
                    disabled={isLoading || !telegramUsername.trim()}
                    className="cracker-btn cracker-btn--gold w-full"
                    onClick={handlePayment}
                  >
                    {isLoading
                      ? 'Creating payment…'
                      : `Pay ${getAddonCryptoPrice(selectedCrypto)} ${selectedCrypto}`}
                  </button>
                </div>
              </>
            ) : (
              <div className="apm-body space-y-5">
                <div className="text-center pt-2">
                  <p className="cracker-label mb-2">Pending</p>
                  <h3 className="cracker-heading text-base mb-2">Crypto payment</h3>
                  <p className="text-sm text-[#a3a3a3]">
                    Send exactly{' '}
                    <span className="text-[#e53935] font-semibold font-mono">
                      {paymentData.paymentAmount} {paymentData.paymentCurrency}
                    </span>
                  </p>
                </div>

                <div className="apm-timer">
                  Time remaining — {formatTime(timeLeft)}
                  {timeLeft <= 300 && (
                    <p className="text-xs mt-1 opacity-90">Payment expiring soon</p>
                  )}
                </div>

                <div>
                  <label className="apm-field-label">Payment address</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={paymentData.paymentAddress}
                      readOnly
                      className="apm-input font-mono text-xs flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(paymentData.paymentAddress)}
                      className="dashboard-btn dashboard-btn--ghost shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="apm-summary">
                  <h4 className="cracker-section-title mb-2">Instructions</h4>
                  <ul className="apm-features">
                    <li>
                      Montant exact : {paymentData.paymentAmount} {paymentData.paymentCurrency}
                    </li>
                    <li>Automatic confirmation on receipt</li>
                    <li>Telegram notification with your access</li>
                    <li>Status checked every 10 seconds</li>
                  </ul>
                </div>

                <div className="text-center pb-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="dashboard-btn dashboard-btn--ghost text-xs"
                  >
                    Close (payment continues in background)
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddonPurchaseModal;
